import { GroupeCreateInput, GroupeListResponse, GroupeResponse, GroupeSelectInput, GroupeUpdateInput } from "@repo/types";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { services } from '../../../lib/api/services';
import { useQueryWithConfig } from '../useQueryWithConfig';

// Hooks for fetching groups
export const useGroupes = (filters: GroupeSelectInput) => {
  const { page = 1, limit = 10, sortBy, ...filterParams } = filters;

  // Handle search terms efficiently - prepare search params based on what's available
  const searchParams = Object.fromEntries(
    Object.entries(filterParams).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  );

  const keys = [
    "groupes",
    "list",
    page,
    limit,
    sortBy,
    sortBy,
    Object.keys(searchParams).length > 0 ? JSON.stringify(searchParams) : null
  ].filter(Boolean);

  return useQueryWithConfig<GroupeListResponse>(
    keys as string[],
    () => services.groupe.getGroupes({
      page,
      limit,
      sortBy,
      ...searchParams
    }),
    {
      // Avoid unnecessary refetches when search parameters haven't actually changed
      staleTime: 1000 * 60, // 1 minute
    }
  );
};

export const useGroupe = (id: number) => {
  return useQueryWithConfig<GroupeResponse>(
    ["groupes", "detail", id],
    () => services.groupe.getGroupeById(id),
    {
      enabled: !!id,
    }
  );
};

export const useGroupeOptions = (filters: GroupeSelectInput) => {
  return useQueryWithConfig<GroupeListResponse>(
    ["groupes", "options"],
    () => services.groupe.getGroupesSelect(filters)
  );
};

// Hooks for group mutations
export const useCreateGroupe = () => {
  const queryClient = useQueryClient();

  return useMutation<GroupeResponse, Error, GroupeCreateInput>({
    mutationFn: (data) => services.groupe.createGroupe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groupes"] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "options"] });
    },
  });
};

export const useUpdateGroupe = () => {
  const queryClient = useQueryClient();

  return useMutation<GroupeResponse, Error, { id: number; data: GroupeUpdateInput }>({
    mutationFn: ({ id, data }) => services.groupe.updateGroupe(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["groupes", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "options"] });
    },
  });
};

export const useDeleteGroupe = () => {
  const queryClient = useQueryClient();

  return useMutation<GroupeResponse, Error, number>({
    mutationFn: (id) => services.groupe.deleteGroupe(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["groupes", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "list"] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "options"] });
    },
  });
};

export const useActivateGroupe = () => {
  const queryClient = useQueryClient();

  return useMutation<GroupeResponse, Error, number>({
    mutationFn: (id) => services.groupe.activateGroupe(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["groupes", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "list"] });
    },
  });
};

export const useToggleStrippedStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<GroupeResponse, Error, { id: number; isStripped?: boolean }>({
    mutationFn: ({ id, isStripped }) => services.groupe.toggleStrippedStatus(id, isStripped),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["groupes", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "list"] });
    },
  });
};

export const useUploadGroupeLogo = () => {
  const queryClient = useQueryClient();

  return useMutation<string, Error, { groupeId: number; file: File }>({
    mutationFn: ({ groupeId, file }) => services.groupe.uploadLogo(groupeId, file),
    onSuccess: (_, { groupeId }) => {
      queryClient.invalidateQueries({ queryKey: ["groupes", "detail", groupeId] });
      queryClient.invalidateQueries({ queryKey: ["groupes", "list"] });
    },
  });
};
